import { NodeProps } from 'reactflow';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { maskDecimal, maskWholeNumber } from '@givebutter/util';
import { faBolt } from '@fortawesome/pro-solid-svg-icons';
import { CampaignBadges } from 'assets/js/pages/engage/workflows/components/CampaignBadges';
import {
    CampaignSelect,
    defaultCampaignSelectValue,
} from 'assets/js/pages/engage/workflows/components/CampaignSelect';
import * as CanvasNode from 'assets/js/pages/engage/workflows/components/CanvasNode';
import * as NodeBadges from 'assets/js/pages/engage/workflows/components/NodeBadges';
import { StepFormContainer } from 'assets/js/pages/engage/workflows/components/StepFormContainer';
import { useDebouncedAutosave } from 'assets/js/pages/engage/workflows/hooks/useDebouncedAutosave';
import { useSyncFormToNode } from 'assets/js/pages/engage/workflows/hooks/useSyncFormToNode';
import { useTriggerValidationOnDeselection } from 'assets/js/pages/engage/workflows/hooks/useTriggerValidationOnDeselection';
import {
    TeamMemberMilestoneTriggerFormValues,
    TeamMemberMilestoneTriggerNodeData,
} from 'assets/js/pages/engage/workflows/triggers/types';
import { enabledTriggers } from 'assets/js/pages/engage/workflows/utils/starterItems';
import { transformOperatorToReadable } from 'components/filter/filters-next/readable';
import * as Form from 'components/form';
import { InputCurrencyIcon } from 'components/input';
import { WorkflowTriggerTypeEnum } from 'constants/workflows';
import { CurrencySymbol } from 'customHooks/useCurrencySymbol';

interface TeamMemberMilestoneTriggerProps extends NodeProps<TeamMemberMilestoneTriggerNodeData> {}
const TeamMemberMilestoneTrigger = ({ id, selected, data }: TeamMemberMilestoneTriggerProps) => {
    const { formData } = data;
    const methods = Form.useGivebutterForm<TeamMemberMilestoneTriggerFormValues>({
        mode: 'all',
        defaultValues: {
            campaigns: formData?.values.campaigns || defaultCampaignSelectValue,
            amountRaised: formData?.values.amountRaised || '',
            amountRaisedEnd: formData?.values.amountRaisedEnd || '',
            amountRaisedOperator: formData?.values.amountRaisedOperator || 'gt',
            totalSupporters: formData?.values.totalSupporters || '',
            totalSupportersEnd: formData?.values.totalSupportersEnd || '',
            totalSupportersOperator: formData?.values.totalSupportersOperator || 'gt',
        },
    });
    const {
        control,
        formState: { errors },
        watch,
        path,
        getValues,
        trigger,
    } = methods;
    const isError = Object.keys(errors).length > 0;
    const { amountRaisedOperator, totalSupportersOperator } = watch();

    useSyncFormToNode<TeamMemberMilestoneTriggerFormValues>({ nodeId: id, control });
    useDebouncedAutosave<TeamMemberMilestoneTriggerFormValues>({ methods });
    useTriggerValidationOnDeselection<TeamMemberMilestoneTriggerFormValues>({ trigger, selected });

    const selectedTrigger = enabledTriggers.find(
        t => t.slug === WorkflowTriggerTypeEnum.TEAM_MEMBER_MILESTONE,
    );
    if (!selectedTrigger) return;

    return (
        <Form.Provider {...methods}>
            <StepFormContainer>
                <CanvasNode.Root
                    className="max-w-[460px]"
                    isSelected={selected}
                    isError={isError}
                    id={id}
                >
                    <CanvasNode.Container>
                        <CanvasNode.Header>
                            <CanvasNode.Icon icon={faBolt} className="text-yellow-300" />
                            <div className="flex flex-col items-start">
                                <CanvasNode.Title>{selectedTrigger.name}</CanvasNode.Title>
                                {selected ? (
                                    <CanvasNode.Description>
                                        Configure your workflow trigger
                                    </CanvasNode.Description>
                                ) : null}
                            </div>
                        </CanvasNode.Header>
                        <CanvasNode.Body>
                            <CanvasNode.BodySelected>
                                <CampaignSelect />
                                <Form.Container>
                                    <Form.Label name={path('amountRaised')}>
                                        Amount raised{' '}
                                        <span className="text-gray-400">(optional)</span>
                                    </Form.Label>
                                    <div className="flex items-center">
                                        <Form.SingleSelect
                                            control={control}
                                            name="amountRaisedOperator"
                                            onValueChange={() => trigger()}
                                        >
                                            <SingleSelect.Trigger
                                                aria-label="Amount raised operator"
                                                className="basis-max-content input-left"
                                            />
                                            <SingleSelect.Content>
                                                <SingleSelect.Item value="gt">
                                                    <SingleSelect.ItemText>
                                                        Greater than
                                                    </SingleSelect.ItemText>
                                                </SingleSelect.Item>
                                                <SingleSelect.Item value="eq">
                                                    <SingleSelect.ItemText>
                                                        Equals
                                                    </SingleSelect.ItemText>
                                                </SingleSelect.Item>
                                                <SingleSelect.Item value="lt">
                                                    <SingleSelect.ItemText>
                                                        Less than
                                                    </SingleSelect.ItemText>
                                                </SingleSelect.Item>
                                                <SingleSelect.Item value="bet">
                                                    <SingleSelect.ItemText>
                                                        Between
                                                    </SingleSelect.ItemText>
                                                </SingleSelect.Item>
                                            </SingleSelect.Content>
                                        </Form.SingleSelect>
                                        <div className="flex min-w-0 items-center gap-2">
                                            <Form.TextInputRoot
                                                control={control}
                                                name="amountRaised"
                                                inputRootProps={{
                                                    mask: 'decimal',
                                                    className: 'input-right min-w-0',
                                                }}
                                            >
                                                <InputCurrencyIcon />
                                                <Form.TextInputForm />
                                            </Form.TextInputRoot>
                                            {amountRaisedOperator === 'bet' ? (
                                                <>
                                                    <span>and</span>
                                                    <Form.TextInputRoot
                                                        control={control}
                                                        name="amountRaisedEnd"
                                                        rules={{
                                                            required:
                                                                amountRaisedOperator === 'bet' &&
                                                                'End amount is required.',
                                                        }}
                                                        inputRootProps={{
                                                            mask: 'decimal',
                                                            className: 'min-w-0',
                                                        }}
                                                    >
                                                        <InputCurrencyIcon />
                                                        <Form.TextInputForm />
                                                    </Form.TextInputRoot>
                                                </>
                                            ) : null}
                                        </div>
                                    </div>
                                    <Form.Error name="amountRaisedEnd" />
                                </Form.Container>
                                <Form.Container>
                                    <Form.Label name={path('totalSupporters')}>
                                        Supporters <span className="text-gray-400">(optional)</span>
                                    </Form.Label>
                                    <div className="flex items-center">
                                        <Form.SingleSelect
                                            control={control}
                                            name="totalSupportersOperator"
                                            onValueChange={() => trigger()}
                                        >
                                            <SingleSelect.Trigger
                                                aria-label="Total supporters operator"
                                                className="basis-max-content input-left"
                                            />
                                            <SingleSelect.Content>
                                                <SingleSelect.Item value="gt">
                                                    <SingleSelect.ItemText>
                                                        Greater than
                                                    </SingleSelect.ItemText>
                                                </SingleSelect.Item>
                                                <SingleSelect.Item value="eq">
                                                    <SingleSelect.ItemText>
                                                        Equals
                                                    </SingleSelect.ItemText>
                                                </SingleSelect.Item>
                                                <SingleSelect.Item value="lt">
                                                    <SingleSelect.ItemText>
                                                        Less than
                                                    </SingleSelect.ItemText>
                                                </SingleSelect.Item>
                                                <SingleSelect.Item value="bet">
                                                    <SingleSelect.ItemText>
                                                        Between
                                                    </SingleSelect.ItemText>
                                                </SingleSelect.Item>
                                            </SingleSelect.Content>
                                        </Form.SingleSelect>
                                        <div className="flex min-w-0 items-center gap-2">
                                            <Form.TextInputRoot
                                                control={control}
                                                name="totalSupporters"
                                                inputRootProps={{
                                                    mask: 'whole-number',
                                                    className: 'input-right min-w-0',
                                                }}
                                            >
                                                <Form.TextInputForm />
                                            </Form.TextInputRoot>
                                            {totalSupportersOperator === 'bet' ? (
                                                <>
                                                    <span>and</span>
                                                    <Form.TextInputRoot
                                                        control={control}
                                                        name="totalSupportersEnd"
                                                        rules={{
                                                            required:
                                                                totalSupportersOperator === 'bet' &&
                                                                'End amount is required.',
                                                        }}
                                                        inputRootProps={{
                                                            mask: 'whole-number',
                                                            className: 'min-w-0',
                                                        }}
                                                    >
                                                        <Form.TextInputForm />
                                                    </Form.TextInputRoot>
                                                </>
                                            ) : null}
                                        </div>
                                    </div>
                                    <Form.Error name="totalSupportersEnd" />
                                </Form.Container>
                            </CanvasNode.BodySelected>
                            <CanvasNode.BodyUnselected>
                                <TeamMemberMilestoneFormValuesDisplay values={getValues()} />
                            </CanvasNode.BodyUnselected>
                        </CanvasNode.Body>
                    </CanvasNode.Container>
                </CanvasNode.Root>
            </StepFormContainer>
        </Form.Provider>
    );
};

interface TeamMemberMilestoneFormValuesDisplayProps {
    values: TeamMemberMilestoneTriggerFormValues;
    showLabel?: boolean;
}
const TeamMemberMilestoneFormValuesDisplay = ({
    values,
    showLabel = false,
}: TeamMemberMilestoneFormValuesDisplayProps) => {
    const {
        campaigns,
        amountRaised,
        amountRaisedEnd,
        amountRaisedOperator,
        totalSupporters,
        totalSupportersEnd,
        totalSupportersOperator,
    } = values;
    const trigger = enabledTriggers.find(
        t => t.slug === WorkflowTriggerTypeEnum.TEAM_MEMBER_MILESTONE,
    );
    if (!trigger) return null;

    return (
        <div className="flex flex-col gap-2">
            {showLabel ? <p className="text-sm font-medium text-gray-900">{trigger.name}</p> : null}
            <NodeBadges.Root>
                <CampaignBadges campaigns={campaigns} />
                {amountRaised ? (
                    <>
                        <NodeBadges.Separator>AND</NodeBadges.Separator>
                        <NodeBadges.Badge>
                            Amount raised{' '}
                            <span className="text-gray-900">
                                {transformOperatorToReadable(amountRaisedOperator)}{' '}
                                {amountRaisedOperator === 'bet' ? (
                                    <>
                                        <span className="text-gray-900">
                                            <CurrencySymbol />
                                            {maskDecimal(amountRaised)}
                                        </span>{' '}
                                        and{' '}
                                        <span className="text-gray-900">
                                            <CurrencySymbol />
                                            {maskDecimal(amountRaisedEnd)}
                                        </span>
                                    </>
                                ) : (
                                    <span className="text-gray-900">
                                        <CurrencySymbol />
                                        {maskDecimal(amountRaised)}
                                    </span>
                                )}
                            </span>
                        </NodeBadges.Badge>
                    </>
                ) : null}

                {totalSupporters ? (
                    <>
                        <NodeBadges.Separator>AND</NodeBadges.Separator>
                        <NodeBadges.Badge>
                            Supporters{' '}
                            <span className="text-gray-900">
                                {transformOperatorToReadable(totalSupportersOperator)}{' '}
                                {totalSupportersOperator === 'bet' ? (
                                    <>
                                        <span className="text-gray-900">
                                            {maskWholeNumber(totalSupporters)}
                                        </span>{' '}
                                        and{' '}
                                        <span className="text-gray-900">
                                            {maskWholeNumber(totalSupportersEnd)}
                                        </span>
                                    </>
                                ) : (
                                    <span className="text-gray-900">
                                        {maskWholeNumber(totalSupporters)}
                                    </span>
                                )}
                            </span>
                        </NodeBadges.Badge>
                    </>
                ) : null}
            </NodeBadges.Root>
        </div>
    );
};

export { TeamMemberMilestoneTrigger, TeamMemberMilestoneFormValuesDisplay };
